import React, { useEffect, useState } from 'react';
import { View, ActivityIndicator, Text, StyleSheet, Platform, Linking } from 'react-native';
import { Colors } from '@/constants/colors';
import { handleOAuthCallback } from '@/services/auth/authService';
import { useAuthStore } from '@/store/authStore';

interface AuthCallbackScreenProps {
  navigation: any;
  route?: any;
}

/**
 * AuthCallbackScreen
 * Handles Google OAuth redirects
 *
 * This screen is opened when:
 * - User completes Google OAuth flow and is redirected back to the app
 */
export const AuthCallbackScreen: React.FC<AuthCallbackScreenProps> = ({ navigation, route }) => {
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('Completing sign-in...');
  const [handled, setHandled] = useState(false);
  const checkAuth = useAuthStore(state => state.checkAuth);

  useEffect(() => {
    const handleCallback = async (incomingUrl?: string) => {
      try {
        setMessage('Verifying authentication...');

        // Pull possible params from route when present
        const params = (route?.params as any) ?? {};
        const routeCode = params.code || params?.params?.code;
        const urlToUse = incomingUrl || params.url;

        // Handle OAuth callback (works for both web and mobile)
        const result = await handleOAuthCallback({ url: urlToUse, code: routeCode });

        if (!result.success) {
          throw new Error(result.error || 'Authentication failed');
        }

        setMessage('Success! Redirecting...');

        // Refresh auth state - this will trigger root navigator to switch to Main
        await checkAuth();

        // Root navigator will automatically show Main stack when isAuthenticated becomes true
        // No manual navigation needed!
      } catch (e: any) {
        console.error('Auth callback error:', e);
        setError(e?.message || 'Authentication failed');

        // Redirect to login after error
        setTimeout(() => {
          navigation.replace('Login');
        }, 3000);
      }
    };

    // Handle immediately for cold start / when route params carry code/url
    if (!handled) {
      setHandled(true);
      handleCallback();
    }

    // Also subscribe for warm-start deep links
    const sub = Linking.addEventListener('url', ({ url }) => {
      handleCallback(url);
    });

    return () => {
      // @ts-expect-error - typing differences across RN versions
      sub?.remove?.();
    };
  }, [navigation, checkAuth, route, handled]);

  return (
    <View style={styles.container}>
      {!error && <ActivityIndicator size="large" color={Colors.accent.primary} />}

      <Text style={[styles.text, error && styles.errorText]}>
        {error ? `Error: ${error}` : message}
      </Text>

      {error && <Text style={styles.subText}>Redirecting to login...</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.background.primary,
  },
  text: {
    marginTop: 12,
    color: Colors.text.secondary,
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  errorText: {
    color: Colors.status.error,
  },
  subText: {
    marginTop: 8,
    color: Colors.text.tertiary,
    fontSize: 12,
    textAlign: 'center',
  },
});
